import React, { useRef } from "react";
import { Typography } from "../ui/typography";
import { Button } from "../ui/button";
import { LayoutType } from "./LayoutContainer";
import addIcon from "@/assets/svg/add.svg";
import addIconLight from "@/assets/svg/addLight.svg";
import Icon100 from "@/assets/svg/100-light.svg?react";
import Icon5050 from "@/assets/svg/50-50-light.svg?react";
import Icon33 from "@/assets/svg/33-light.svg?react";
import Icon7030 from "@/assets/svg/70-30-light.svg?react";
import Icon3070 from "@/assets/svg/30-70-light.svg?react";
import DoneIcon from "@/assets/svg/doneIcon.svg";
import RestoreLayoutDialogBox from "./RestoreLayoutDialogBox";
import { TruncatedTextTooltip } from "@/components/common/TextTooltip";

const LAYOUT_ICONS: { type: LayoutType; label: string; Icon: React.FC<React.SVGProps<SVGSVGElement>> }[] = [
  { type: "100", label: "Full Width", Icon: Icon100 },
  { type: "50-50", label: "50 / 50", Icon: Icon5050 },
  { type: "3-column", label: "3 Columns", Icon: Icon33 },
  { type: "70-30", label: "70 / 30", Icon: Icon7030 },
  { type: "30-70", label: "30 / 70", Icon: Icon3070 },
];

interface EditDashboardHeaderProps {
  layout: LayoutType;
  onLayoutChange: (layout: LayoutType) => void;
  onCancel?: () => void;
  onToggleDrawer?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  isSaveDisabled?: boolean;
  onRestore?: (type: "default" | "preferred") => Promise<void>;
  isToggling?: boolean;
  /** Show Default Layout in the Restore dropdown (false when user is already on default and preferred exists). */
  showDefaultRestore?: boolean;
  /** Show Preferred Layout in the Restore dropdown (true only when preferred layout is saved and user is on default). */
  showPreferredRestore?: boolean;
  /** True while the preferred layout is being fetched on mount — disables all header controls. */
  isLoading?: boolean;
  /** True when the user has unsaved changes — enables the Save button. */
  hasChanges?: boolean;
  /** False when the user has never saved a preferred layout — disables the Restore Layout button entirely. */
  hasPreferredLayout?: boolean;
}

const EditDashboardHeader: React.FC<EditDashboardHeaderProps> = ({
  layout,
  onLayoutChange,
  onCancel,
  onToggleDrawer,
  onSave,
  isSaving = false,
  isSaveDisabled = false,
  isLoading = false,
  hasChanges = false,
  onRestore,
  isToggling = false,
  showDefaultRestore = true,
  showPreferredRestore = false,
  hasPreferredLayout = true,
}) => {
  const isDisabled = isLoading || isSaving;
  const headerRef = useRef<HTMLDivElement>(null);

  const disabledCursorStyle: React.CSSProperties = {
    cursor: [
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Ccircle cx='10' cy='10' r='8' fill='none' stroke='%236b7280' stroke-width='2.5'/%3E%3Cline x1='3.64' y1='3.64' x2='16.36' y2='16.36' stroke='%236b7280' stroke-width='2.5'/%3E%3C/svg%3E\") 10 10",
      "not-allowed",
    ].join(", "),
  };

  return (
    <>
      <div
        ref={headerRef}
        className="flex flex-col w-full py-1 sm:py-3 md:py-2 lg:py-2"
      >
        <div className="flex items-center justify-between gap-2 w-full min-h-[48px] md:min-h-[60px]">
        <Typography variant="bold-22" className="text-3xl whitespace-nowrap">Edit Dashboard</Typography>
        <div className="flex items-center gap-3">
          <RestoreLayoutDialogBox
            showDefault={showDefaultRestore}
            showPreferred={showPreferredRestore}
            onRestore={onRestore}
            isRestoring={isDisabled || isToggling}
            disabled={!hasPreferredLayout}
          />

          {/* Layout type selector */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              borderRadius: "8px",
              padding: "4px",
            }}
            className={`border border-grey-300 dark:border-grey-600 ${isDisabled ? "pointer-events-none opacity-50" : ""}`}
          >
            {LAYOUT_ICONS.map(({ type, label, Icon }) => {
              const isActive = layout === type;
              return (
                <TruncatedTextTooltip key={type} text={label} alwaysShow>
                  <div
                    onClick={() => onLayoutChange(type)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    className={
                      isActive
                        ? "bg-primary-100 dark:bg-primary-800 text-primary-600 dark:text-primary-400"
                        : "bg-transparent hover:bg-grey-100 dark:hover:bg-transparent text-grey-900 dark:text-white"
                    }
                  >
                    <Icon width={20} height={20} />
                  </div>
                </TruncatedTextTooltip>
              );
            })}
          </div>

          {/* Cancel button */}
          <span style={isDisabled ? disabledCursorStyle : undefined}>
          <Button variant='outline2' size='md' onClick={onCancel} disabled={isDisabled}>
            <Typography variant='medium-14' className='text-grey-800 dark:text-grey-100'>Cancel</Typography>
          </Button>
          </span>

          {/* Add Cards button */}
          <span style={isDisabled ? disabledCursorStyle : undefined}>
          <Button
            variant='outline2'
            size='md'
            className='w-[137px] h-[40px] flex items-center gap-2'
            onClick={onToggleDrawer}
            label="Add Cards"
            disabled={isDisabled}
            startIcon={
              <>
                <img src={addIcon} alt='add' className='w-3 h-3 dark:hidden' />
                <img src={addIconLight} alt='add' className='w-3 h-3 hidden dark:block' />
              </>
            }
          />
        </span>
        <span style={(isDisabled || isSaveDisabled || !hasChanges) ? disabledCursorStyle : undefined}>
          <Button
            variant='contained1'
            size='md'
            className='w-[102px] h-[40px] flex items-center gap-2'
            label="Save"
            disabled={isDisabled || isSaveDisabled || !hasChanges}
            onClick={onSave}
            startIcon={<img src={DoneIcon} alt='done' className='w-3 h-3' />}
          />
        </span>
        </div>
        </div>
      </div>
    </>
  );
};

export default EditDashboardHeader;
